import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Plus, Phone, Mail, MapPin, User, Briefcase, Camera, CheckCircle2, Loader2, Search, Trash2, Eye, CreditCard, ScanLine, X, TrendingDown, Video, MapPinned, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  qualified: 'bg-emerald-100 text-emerald-700',
  converted: 'bg-purple-100 text-purple-700',
  lost: 'bg-red-100 text-red-700',
};

const FUNNEL_STAGES = [
  { key: 'new', label: 'New', color: '#3B82F6' },
  { key: 'contacted', label: 'Contacted', color: '#F59E0B' },
  { key: 'qualified', label: 'Qualified', color: '#10B981' },
  { key: 'converted', label: 'Converted', color: '#8B5CF6' },
];
const LOST_COLOR = '#EF4444';

export default function Leads() {
  const { api, currentUser, selectedBusiness } = useApp();
  const [leads, setLeads] = useState([]);
  const [potentialBusinesses, setPotentialBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ocrLoading, setOcrLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [mockOtp, setMockOtp] = useState('');
  const fileInputRef = useRef(null);

  // Contact proof state
  const [proofDialogOpen, setProofDialogOpen] = useState(false);
  const [proofStep, setProofStep] = useState('choose'); // 'choose', 'physical', 'call'
  const [proofSelfie, setProofSelfie] = useState(null);
  const [proofLat, setProofLat] = useState(null);
  const [proofLng, setProofLng] = useState(null);
  const [proofGeoLoading, setProofGeoLoading] = useState(false);
  const [proofRecording, setProofRecording] = useState(null);
  const [proofRecordingName, setProofRecordingName] = useState('');
  const [proofSubmitting, setProofSubmitting] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const recordingInputRef = useRef(null);

  const emptyForm = {
    customer_name: '', email: '', mobile: '', mobile_verified: false,
    customer_location: '', visiting_card_image: null,
    potential_business_id: '', potential_business_label: '',
    business_id: '', notes: '',
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { user_id: currentUser?.id, role: currentUser?.role };
      if (selectedBusiness) params.business_id = selectedBusiness;
      const [leadsRes, pbRes] = await Promise.all([
        api.getLeads(params),
        api.getPotentialBusinesses({ status: 'active' }),
      ]);
      setLeads(leadsRes.data.data || leadsRes.data);
      setPotentialBusinesses(pbRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, currentUser, selectedBusiness]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ ...emptyForm, business_id: selectedBusiness || '' });
    setOtpSent(false);
    setOtpCode('');
    setMockOtp('');
    setDialogOpen(true);
  };

  const handleCardUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(',')[1];
      setForm(prev => ({ ...prev, visiting_card_image: b64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleOcr = async () => {
    if (!form.visiting_card_image) { toast.error('Upload a visiting card first'); return; }
    setOcrLoading(true);
    try {
      const res = await api.ocrVisitingCard(form.visiting_card_image);
      if (res.data.success) {
        const d = res.data.data;
        setForm(prev => ({
          ...prev,
          customer_name: d.name || prev.customer_name,
          email: d.email || prev.email,
          mobile: d.phone || prev.mobile,
          customer_location: d.address || prev.customer_location,
        }));
        toast.success('Card details extracted successfully');
      } else {
        toast.error('Could not extract details: ' + (res.data.error || 'Unknown error'));
      }
    } catch (e) { toast.error('OCR failed'); }
    setOcrLoading(false);
  };

  const handleSendOtp = async () => {
    if (!form.mobile || form.mobile.length < 10) { toast.error('Enter a valid mobile number'); return; }
    setOtpSending(true);
    try {
      const res = await api.sendOtp(form.mobile);
      setMockOtp(res.data.otp_code);
      setOtpSent(true);
      toast.success(`OTP sent! (Mock OTP: ${res.data.otp_code})`);
    } catch (e) { toast.error('Failed to send OTP'); }
    setOtpSending(false);
  };

  const handleVerifyOtp = async () => {
    if (!otpCode) { toast.error('Enter the OTP'); return; }
    setOtpVerifying(true);
    try {
      const res = await api.verifyOtp(form.mobile, otpCode);
      if (res.data.verified) {
        setForm(prev => ({ ...prev, mobile_verified: true }));
        toast.success('Mobile verified!');
      }
    } catch (e) { toast.error('Invalid OTP'); }
    setOtpVerifying(false);
  };

  const handleSubmit = async () => {
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return; }
    if (!form.mobile.trim() || form.mobile.trim().length < 10) { toast.error('Valid mobile number is required'); return; }
    try {
      await api.createLead(form, currentUser?.id);
      toast.success('Lead added successfully');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error('Failed to create lead'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try { await api.deleteLead(id); toast.success('Lead deleted'); load(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const handleStatusChange = async (id, newStatus) => {
    // Intercept "contacted" transition - require proof
    if (newStatus === 'contacted' && selectedLead?.status === 'new') {
      setProofStep('choose');
      setProofSelfie(null);
      setProofLat(null);
      setProofLng(null);
      setProofRecording(null);
      setProofRecordingName('');
      setCameraActive(false);
      setProofDialogOpen(true);
      return;
    }
    try {
      await api.updateLead(id, { status: newStatus });
      toast.success('Status updated');
      load();
      if (selectedLead?.id === id) setSelectedLead(prev => ({ ...prev, status: newStatus }));
    } catch (e) { toast.error('Failed to update'); }
  };

  // === Contact Proof Methods ===
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      setCameraStream(stream);
      setCameraActive(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch (e) {
      toast.error('Camera access denied. Please allow camera permissions.');
    }
  };

  const captureSelfie = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const b64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
    setProofSelfie(b64);
    // Stop camera
    if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
    setCameraStream(null);
    setCameraActive(false);
  };

  const getGeoLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setProofGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setProofLat(pos.coords.latitude); setProofLng(pos.coords.longitude); setProofGeoLoading(false); toast.success('Location captured'); },
      (err) => { setProofGeoLoading(false); toast.error('Could not get location: ' + err.message); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleRecordingUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error('File too large. Max 50MB.'); return; }
    setProofRecordingName(file.name);
    const reader = new FileReader();
    reader.onload = () => setProofRecording(reader.result.split(',')[1]);
    reader.readAsDataURL(file);
  };

  const submitContactProof = async () => {
    if (!selectedLead) return;
    if (proofStep === 'physical' && !proofSelfie) { toast.error('Please capture a selfie'); return; }
    if (proofStep === 'call' && !proofRecording) { toast.error('Please upload a screen recording'); return; }
    setProofSubmitting(true);
    try {
      const updateData = {
        status: 'contacted',
        contact_method: proofStep,
        contact_proof_at: new Date().toISOString(),
      };
      if (proofStep === 'physical') {
        updateData.contact_selfie = proofSelfie;
        updateData.contact_lat = proofLat;
        updateData.contact_lng = proofLng;
      } else {
        updateData.contact_recording = proofRecording;
      }
      await api.updateLead(selectedLead.id, updateData);
      toast.success('Lead marked as contacted with proof');
      setProofDialogOpen(false);
      // Cleanup camera if still running
      if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
      load();
      setSelectedLead(prev => ({ ...prev, status: 'contacted', contact_method: proofStep }));
    } catch (e) { toast.error('Failed to update lead'); }
    setProofSubmitting(false);
  };

  const closeProofDialog = () => {
    if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
    setCameraStream(null);
    setCameraActive(false);
    setProofDialogOpen(false);
  };

  const filtered = leads.filter(l => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (l.customer_name || '').toLowerCase().includes(s) ||
        (l.email || '').toLowerCase().includes(s) ||
        (l.mobile || '').includes(s) ||
        (l.customer_location || '').toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div data-testid="leads-page" className="space-y-6 fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">{leads.length} total leads captured</p>
        </div>
        <Button data-testid="add-lead-btn" onClick={openCreate} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-md">
          <Plus className="w-4 h-4" /> Add Lead
        </Button>
      </div>

      <InstructionGuide
        title="How Leads work"
        description="Capture customer leads from field visits. Upload visiting cards for auto-fill, verify mobile numbers, and track lead status."
        steps={[
          'Click "Add Lead" to capture a new customer lead.',
          'Optionally upload a visiting card and use OCR to auto-fill contact details.',
          'Verify the mobile number with OTP (simulated).',
          'Select the potential business category and save.',
          'Track and update lead status: New > Contacted > Qualified > Converted.',
        ]}
        flow={['Add Lead', 'Fill Details / OCR', 'Verify Mobile', 'Save Lead', 'Track Status']}
      />

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="lead-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, mobile, location..." className="pl-10" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="status-filter" className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="qualified">Qualified</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Conversion Funnel */}
      {!loading && leads.length > 0 && (() => {
        const counts = { new: 0, contacted: 0, qualified: 0, converted: 0, lost: 0 };
        leads.forEach(l => { if (counts[l.status] !== undefined) counts[l.status]++; });
        const total = leads.length;
        const funnelData = FUNNEL_STAGES.map(s => ({ ...s, count: counts[s.key], pct: total ? Math.round((counts[s.key] / total) * 100) : 0 }));
        const conversionRate = total ? Math.round((counts.converted / total) * 100) : 0;

        return (
          <Card data-testid="lead-funnel" className="border border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-amber-600" /> Conversion Funnel
                </CardTitle>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-purple-600">{conversionRate}%</p>
                    <p className="text-[10px] text-slate-400 uppercase font-bold">Conversion</p>
                  </div>
                  {counts.lost > 0 && (
                    <div className="text-right">
                      <p className="text-lg font-bold text-red-500">{counts.lost}</p>
                      <p className="text-[10px] text-slate-400 uppercase font-bold">Lost</p>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Visual Funnel */}
              <div className="flex items-end gap-1 mb-4" style={{ height: 120 }}>
                {funnelData.map((stage, i) => {
                  const maxCount = Math.max(...funnelData.map(s => s.count), 1);
                  const barH = Math.max((stage.count / maxCount) * 100, 8);
                  const width = 100 - (i * 12);
                  return (
                    <div key={stage.key} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs font-bold text-slate-700">{stage.count}</span>
                      <div className="w-full flex justify-center">
                        <div
                          data-testid={`funnel-bar-${stage.key}`}
                          className="rounded-t-md transition-all duration-500"
                          style={{ height: barH, width: `${width}%`, backgroundColor: stage.color, minHeight: 8 }}
                        />
                      </div>
                      <span className="text-[10px] font-semibold text-slate-500">{stage.label}</span>
                      <span className="text-[9px] text-slate-400">{stage.pct}%</span>
                    </div>
                  );
                })}
              </div>

              {/* Stage-to-stage rates */}
              <div className="flex items-center gap-2 border-t border-border/30 pt-3">
                {funnelData.map((stage, i) => {
                  if (i === 0) return null;
                  const prev = funnelData[i - 1];
                  const rate = prev.count > 0 ? Math.round((stage.count / prev.count) * 100) : 0;
                  return (
                    <div key={stage.key} className="flex-1 text-center">
                      <div className="flex items-center justify-center gap-1 text-[10px] text-slate-400">
                        <span>{prev.label}</span>
                        <span>→</span>
                        <span>{stage.label}</span>
                      </div>
                      <p className="text-sm font-bold" style={{ color: stage.color }}>{rate}%</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Lead Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border border-border/60"><CardContent className="p-12 text-center">
          <User className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-slate-600">No leads yet</h3>
          <p className="text-sm text-slate-400 mt-1">Click "Add Lead" to capture your first customer lead</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(lead => (
            <Card key={lead.id} data-testid={`lead-card-${lead.id}`} className="border border-border/60 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => { setSelectedLead(lead); setDetailOpen(true); }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge className={`text-[10px] ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`}>{lead.status}</Badge>
                    {lead.mobile_verified && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                  </div>
                </div>
                <h3 className="text-base font-bold text-slate-900 truncate">{lead.customer_name}</h3>
                {lead.potential_business_label && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <Briefcase className="w-3 h-3 text-slate-400" />
                    <span className="text-xs text-slate-500">{lead.potential_business_label}</span>
                  </div>
                )}
                <div className="mt-3 space-y-1">
                  {lead.mobile && <div className="flex items-center gap-1.5 text-xs text-slate-500"><Phone className="w-3 h-3" />{lead.mobile}</div>}
                  {lead.email && <div className="flex items-center gap-1.5 text-xs text-slate-500"><Mail className="w-3 h-3" />{lead.email}</div>}
                  {lead.customer_location && <div className="flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="w-3 h-3" />{lead.customer_location}</div>}
                </div>
                <p className="text-[10px] text-slate-400 mt-3">{new Date(lead.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Lead Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="add-lead-dialog" className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold">Add New Lead</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {/* Visiting Card Upload */}
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-slate-500" /> Visiting Card (Optional)</Label>
                {form.visiting_card_image && (
                  <Button data-testid="ocr-btn" variant="outline" size="sm" onClick={handleOcr} disabled={ocrLoading} className="gap-1.5 text-xs">
                    {ocrLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ScanLine className="w-3 h-3" />}
                    {ocrLoading ? 'Extracting...' : 'Auto-fill from Card'}
                  </Button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleCardUpload} className="hidden" />
              {form.visiting_card_image ? (
                <div className="relative">
                  <img src={`data:image/jpeg;base64,${form.visiting_card_image}`} alt="Visiting Card" className="w-full max-h-40 object-contain rounded-lg border" />
                  <Button variant="ghost" size="sm" onClick={() => { setForm(prev => ({ ...prev, visiting_card_image: null })); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="absolute top-1 right-1 bg-white/80 h-6 w-6 p-0 rounded-full">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <Button data-testid="upload-card-btn" variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full gap-2 border-dashed">
                  <Camera className="w-4 h-4" /> Upload Visiting Card
                </Button>
              )}
            </div>

            {/* Customer Name */}
            <div>
              <Label>Customer Name *</Label>
              <Input data-testid="lead-name" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} placeholder="Full name" />
            </div>

            {/* Email */}
            <div>
              <Label>Email (Optional)</Label>
              <Input data-testid="lead-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" />
            </div>

            {/* Mobile (Mandatory) with optional OTP */}
            <div>
              <Label>Mobile Number *</Label>
              <div className="flex gap-2">
                <Input data-testid="lead-mobile" value={form.mobile} onChange={e => { setForm({ ...form, mobile: e.target.value, mobile_verified: false }); setOtpSent(false); }} placeholder="+91-9876543210" className="flex-1" />
                {form.mobile_verified ? (
                  <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1 px-3"><CheckCircle2 className="w-3 h-3" /> Verified</Badge>
                ) : !otpSent ? (
                  <Button data-testid="send-otp-btn" variant="outline" onClick={handleSendOtp} disabled={otpSending || !form.mobile} className="gap-1.5 whitespace-nowrap">
                    {otpSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Phone className="w-3 h-3" />} Send OTP
                  </Button>
                ) : null}
              </div>
              {otpSent && !form.mobile_verified && (
                <div className="flex gap-2 mt-2">
                  <Input data-testid="otp-input" value={otpCode} onChange={e => setOtpCode(e.target.value)} placeholder="Enter OTP" className="flex-1" maxLength={6} />
                  <Button data-testid="verify-otp-btn" onClick={handleVerifyOtp} disabled={otpVerifying} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
                    {otpVerifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Verify
                  </Button>
                  {mockOtp && <p className="text-[10px] text-slate-400 self-center">Mock OTP: {mockOtp}</p>}
                </div>
              )}
            </div>

            {/* Location */}
            <div>
              <Label>Customer Location</Label>
              <Input data-testid="lead-location" value={form.customer_location} onChange={e => setForm({ ...form, customer_location: e.target.value })} placeholder="City, Area or Address" />
            </div>

            {/* Potential Business */}
            <div>
              <Label>Potential Business</Label>
              <Select value={form.potential_business_id} onValueChange={v => {
                const pb = potentialBusinesses.find(p => p.id === v);
                setForm({ ...form, potential_business_id: v, potential_business_label: pb?.label || '' });
              }}>
                <SelectTrigger data-testid="potential-business-select"><SelectValue placeholder="Select potential business" /></SelectTrigger>
                <SelectContent>
                  {potentialBusinesses.map(pb => (
                    <SelectItem key={pb.id} value={pb.id}>{pb.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea data-testid="lead-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes about the lead..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-lead-btn" onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700 text-white">Save Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent data-testid="lead-detail-dialog" className="sm:max-w-lg">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-bold flex items-center gap-2">
                  <User className="w-5 h-5 text-amber-600" /> {selectedLead.customer_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="flex items-center gap-2">
                  <Badge className={`${STATUS_COLORS[selectedLead.status] || STATUS_COLORS.new}`}>{selectedLead.status}</Badge>
                  {selectedLead.mobile_verified && <Badge className="bg-emerald-50 text-emerald-700 gap-1"><CheckCircle2 className="w-3 h-3" /> Mobile Verified</Badge>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {selectedLead.mobile && <div><p className="text-[10px] text-slate-400 uppercase font-bold">Mobile</p><p className="text-sm text-slate-700 flex items-center gap-1"><Phone className="w-3 h-3" /> {selectedLead.mobile}</p></div>}
                  {selectedLead.email && <div><p className="text-[10px] text-slate-400 uppercase font-bold">Email</p><p className="text-sm text-slate-700 flex items-center gap-1"><Mail className="w-3 h-3" /> {selectedLead.email}</p></div>}
                  {selectedLead.customer_location && <div><p className="text-[10px] text-slate-400 uppercase font-bold">Location</p><p className="text-sm text-slate-700 flex items-center gap-1"><MapPin className="w-3 h-3" /> {selectedLead.customer_location}</p></div>}
                  {selectedLead.potential_business_label && <div><p className="text-[10px] text-slate-400 uppercase font-bold">Potential Business</p><p className="text-sm text-slate-700 flex items-center gap-1"><Briefcase className="w-3 h-3" /> {selectedLead.potential_business_label}</p></div>}
                </div>

                {selectedLead.visiting_card_image && (
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase font-bold mb-1">Visiting Card</p>
                    <img src={`data:image/jpeg;base64,${selectedLead.visiting_card_image}`} alt="Visiting Card" className="w-full max-h-48 object-contain rounded-lg border" />
                  </div>
                )}

                {selectedLead.notes && (
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase font-bold">Notes</p>
                    <p className="text-sm text-slate-600">{selectedLead.notes}</p>
                  </div>
                )}

                {/* Contact Proof Info */}
                {selectedLead.contact_method && (
                  <div className="border border-border/40 rounded-lg p-3 bg-slate-50/50">
                    <p className="text-[10px] text-slate-400 uppercase font-bold mb-2 flex items-center gap-1">
                      <UserCheck className="w-3 h-3" /> Contact Proof ({selectedLead.contact_method === 'physical' ? 'Physical Visit' : 'Virtual Call'})
                    </p>
                    {selectedLead.contact_method === 'physical' && (
                      <div className="space-y-2">
                        {selectedLead.contact_selfie && (
                          <img src={`data:image/jpeg;base64,${selectedLead.contact_selfie}`} alt="Contact Selfie" className="w-full max-h-36 object-contain rounded-lg border" />
                        )}
                        {selectedLead.contact_lat && (
                          <p className="text-xs text-slate-500 flex items-center gap-1"><MapPinned className="w-3 h-3" /> GPS: {selectedLead.contact_lat?.toFixed(4)}, {selectedLead.contact_lng?.toFixed(4)}</p>
                        )}
                      </div>
                    )}
                    {selectedLead.contact_method === 'call' && selectedLead.contact_recording && (
                      <Badge className="bg-blue-50 text-blue-700 gap-1"><Video className="w-3 h-3" /> Screen recording uploaded</Badge>
                    )}
                    {selectedLead.contact_proof_at && (
                      <p className="text-[10px] text-slate-400 mt-1">Contacted: {new Date(selectedLead.contact_proof_at).toLocaleString()}</p>
                    )}
                  </div>
                )}

                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-bold mb-2">Update Status</p>
                  <div className="flex flex-wrap gap-2">
                    {['new', 'contacted', 'qualified', 'converted', 'lost'].map(s => (
                      <Button key={s} data-testid={`status-${s}`} variant={selectedLead.status === s ? 'default' : 'outline'} size="sm"
                        className={selectedLead.status === s ? 'bg-amber-600 text-white' : ''}
                        onClick={() => handleStatusChange(selectedLead.id, s)}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>

                <p className="text-[10px] text-slate-400">Created: {new Date(selectedLead.created_at).toLocaleString()}</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact Proof Dialog */}
      <Dialog open={proofDialogOpen} onOpenChange={closeProofDialog}>
        <DialogContent data-testid="contact-proof-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              {proofStep === 'choose' ? 'How did you contact this lead?' : proofStep === 'physical' ? 'Physical Visit Proof' : 'Virtual Call Proof'}
            </DialogTitle>
          </DialogHeader>

          {/* Step: Choose Method */}
          {proofStep === 'choose' && (
            <div className="space-y-3 py-4">
              <p className="text-sm text-slate-500">Select how you approached this customer:</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="proof-physical-btn"
                  onClick={() => setProofStep('physical')}
                  className="border-2 border-border/60 rounded-xl p-5 hover:border-amber-400 hover:bg-amber-50/50 transition-all text-center group"
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-emerald-200 transition-colors">
                    <MapPinned className="w-6 h-6 text-emerald-600" />
                  </div>
                  <p className="font-bold text-slate-800">Physical Visit</p>
                  <p className="text-[11px] text-slate-400 mt-1">Selfie + Geo Location</p>
                </button>
                <button
                  data-testid="proof-call-btn"
                  onClick={() => setProofStep('call')}
                  className="border-2 border-border/60 rounded-xl p-5 hover:border-amber-400 hover:bg-amber-50/50 transition-all text-center group"
                >
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-blue-200 transition-colors">
                    <Video className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="font-bold text-slate-800">Virtual Call</p>
                  <p className="text-[11px] text-slate-400 mt-1">Upload Screen Recording</p>
                </button>
              </div>
            </div>
          )}

          {/* Step: Physical - Selfie + GPS */}
          {proofStep === 'physical' && (
            <div className="space-y-4 py-2">
              {/* Selfie Capture */}
              <div>
                <Label className="flex items-center gap-2 mb-2"><Camera className="w-4 h-4 text-slate-500" /> Selfie *</Label>
                {proofSelfie ? (
                  <div className="relative">
                    <img src={`data:image/jpeg;base64,${proofSelfie}`} alt="Selfie" className="w-full max-h-48 object-contain rounded-lg border" />
                    <Button variant="ghost" size="sm" onClick={() => setProofSelfie(null)} className="absolute top-1 right-1 bg-white/80 h-6 w-6 p-0 rounded-full">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : cameraActive ? (
                  <div className="space-y-2">
                    <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg border bg-black" style={{ maxHeight: 200 }} />
                    <canvas ref={canvasRef} className="hidden" />
                    <Button data-testid="capture-selfie-btn" onClick={captureSelfie} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Camera className="w-4 h-4" /> Capture Selfie
                    </Button>
                  </div>
                ) : (
                  <Button data-testid="start-camera-btn" variant="outline" onClick={startCamera} className="w-full gap-2 border-dashed">
                    <Camera className="w-4 h-4" /> Open Camera
                  </Button>
                )}
              </div>

              {/* GPS Location */}
              <div>
                <Label className="flex items-center gap-2 mb-2"><MapPinned className="w-4 h-4 text-slate-500" /> Geo Location</Label>
                {proofLat ? (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm text-emerald-700 font-medium">{proofLat.toFixed(6)}, {proofLng.toFixed(6)}</span>
                  </div>
                ) : (
                  <Button data-testid="get-location-btn" variant="outline" onClick={getGeoLocation} disabled={proofGeoLoading} className="w-full gap-2 border-dashed">
                    {proofGeoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPinned className="w-4 h-4" />}
                    {proofGeoLoading ? 'Getting location...' : 'Capture Location'}
                  </Button>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setProofStep('choose')}>Back</Button>
                <Button data-testid="submit-physical-proof" onClick={submitContactProof} disabled={proofSubmitting || !proofSelfie} className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white">
                  {proofSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Submit Proof
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step: Call - Screen Recording Upload */}
          {proofStep === 'call' && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="flex items-center gap-2 mb-2"><Video className="w-4 h-4 text-slate-500" /> Screen Recording *</Label>
                <p className="text-xs text-slate-400 mb-3">Upload a screen recording of the virtual call (max 50MB). Supported: MP4, MOV, WebM, AVI</p>
                <input ref={recordingInputRef} type="file" accept="video/*,.mp4,.mov,.webm,.avi" onChange={handleRecordingUpload} className="hidden" />
                {proofRecording ? (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <Video className="w-5 h-5 text-blue-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-blue-700 truncate">{proofRecordingName}</p>
                      <p className="text-[10px] text-blue-500">Ready to upload</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setProofRecording(null); setProofRecordingName(''); }} className="h-6 w-6 p-0">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Button data-testid="upload-recording-btn" variant="outline" onClick={() => recordingInputRef.current?.click()} className="w-full gap-2 border-dashed">
                    <Video className="w-4 h-4" /> Upload Screen Recording
                  </Button>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setProofStep('choose')}>Back</Button>
                <Button data-testid="submit-call-proof" onClick={submitContactProof} disabled={proofSubmitting || !proofRecording} className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white">
                  {proofSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Submit Proof
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
